import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  maxWidth?: string;
};
export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  maxWidth = 'max-w-md'
}) => {
  // Prevent scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);
  return createPortal(<AnimatePresence data-id="element-379">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={onClose} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998]" aria-hidden="true" data-id="element-380" />
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 pointer-events-none" data-id="element-381">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} transition={{
          duration: 0.2,
          ease: 'easeOut'
        }} className={`w-full ${maxWidth} pointer-events-auto`} role="dialog" aria-modal="true" aria-labelledby="modal-title" data-id="element-382">
              <div className="bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col" data-id="element-383">
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between shrink-0" data-id="element-384">
                  <h2 id="modal-title" className="text-lg font-semibold text-brand-text-dark" data-id="element-385">
                    {title}
                  </h2>
                  <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500" aria-label="Close modal" data-id="element-386">
                    <X size={20} data-id="element-387" />
                  </button>
                </div>
                <div className="p-6 overflow-y-auto" data-id="element-388">{children}</div>
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>, document.body);
};